import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Kanban, Search, X } from 'lucide-react';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Skeleton } from '../../components/Skeleton';
import { useAuth } from '../../auth/AuthProvider';
import { humanizeError } from '../../lib/errors';
import { IssuePanel } from '../issue/IssuePanel';
import { PersonalBoardColumnView } from './PersonalBoardColumn';
import { COLUMN_LIMIT_MAX, nextColumnLimit, projectKeyOfIssue } from './personalBoardModel';
import { usePersonalBoard } from './usePersonalBoard';
import styles from './PersonalBoardPage.module.css';

const SEARCH_DEBOUNCE_MS = 300;

/** `/my` — задачи текущего пользователя из всех проектов (UC-MY-01). */
export function PersonalBoardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const q = searchParams.get('q') ?? '';
  const issueKey = searchParams.get('issue');
  const [search, setSearch] = useState(q);
  const [limit, setLimit] = useState(50);

  const patch = useCallback(
    (next: Readonly<Record<string, string | null>>) => {
      setSearchParams(
        (previous) => {
          const params = new URLSearchParams(previous);
          for (const [name, value] of Object.entries(next)) {
            if (value === null || value === '') params.delete(name);
            else params.set(name, value);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const normalized = search.trim();
    if (normalized === q) return;
    const timer = window.setTimeout(() => patch({ q: normalized || null }), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [patch, q, search]);

  useEffect(() => {
    setLimit(50);
  }, [q]);

  const { board, isLoading, isError, error, refetch } = usePersonalBoard({
    q: q.length > 0 ? q : undefined,
    limitPerColumn: limit,
  });

  const openIssue = useCallback((key: string) => patch({ issue: key }), [patch]);
  const total = board?.total ?? 0;
  const canLoadMore = limit < COLUMN_LIMIT_MAX;
  const hasHidden = board?.columns.some((column) => column.count > column.issues.length) ?? false;

  return (
    <div className={styles['page']}>
      <header className={styles['header']}>
        <div className={styles['heading']}>
          <h1 className={styles['title']}>Моя доска</h1>
          <p className={styles['subtitle']}>
            {user?.displayName ? `${user.displayName} · ` : ''}
            Задач: {total}
          </p>
        </div>

        <div className={styles['search']}>
          <Search aria-hidden size={15} />
          <input
            value={search}
            className={styles['searchInput']}
            placeholder="Поиск по заголовку или ключу"
            aria-label="Поиск по моей доске"
            onChange={(event) => setSearch(event.target.value)}
          />
          {search.length > 0 ? (
            <IconButton
              size="sm"
              label="Очистить поиск"
              icon={<X size={14} />}
              onClick={() => {
                setSearch('');
                patch({ q: null });
              }}
            />
          ) : null}
        </div>
      </header>

      {isLoading ? (
        <div className={styles['columns']}>
          <Skeleton variant="card" count={3} />
        </div>
      ) : isError ? (
        <EmptyState
          icon={<Kanban size={20} />}
          title="Не удалось загрузить мою доску"
          description={humanizeError(error).message}
          action={
            <Button variant="primary" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : total === 0 ? (
        <EmptyState
          icon={<Kanban size={20} />}
          title={q.length > 0 ? 'Ничего не найдено' : 'Вам пока не назначено ни одной задачи'}
          description={
            q.length > 0
              ? 'Измените запрос или сбросьте поиск.'
              : 'Здесь появятся задачи, где вы исполнитель, — из всех проектов сразу.'
          }
          action={
            q.length > 0 ? (
              <Button
                variant="primary"
                onClick={() => {
                  setSearch('');
                  patch({ q: null });
                }}
              >
                Сбросить поиск
              </Button>
            ) : (
              <Button variant="primary" onClick={() => navigate('/')}>
                К проектам
              </Button>
            )
          }
        />
      ) : (
        <div className={styles['columns']} data-testid="personal-board">
          {board?.columns.map((column) => (
            <PersonalBoardColumnView
              key={column.category}
              column={column}
              onOpenIssue={openIssue}
              onShowMore={() => setLimit((current) => nextColumnLimit(current))}
              canLoadMore={canLoadMore}
            />
          ))}
        </div>
      )}

      {hasHidden && !canLoadMore ? (
        <p className={styles['limitHint']}>
          Показаны первые {COLUMN_LIMIT_MAX} задач каждой колонки. Уточните поиск, чтобы увидеть
          остальные.
        </p>
      ) : null}

      <IssuePanel
        issueKey={issueKey}
        projectKey={issueKey ? projectKeyOfIssue(issueKey) : ''}
        onClose={() => patch({ issue: null })}
      />
    </div>
  );
}
